import { useState, useEffect, useCallback, useMemo } from 'react';

const VAULT_API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL;
const VAULT_API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

export interface Category {
  id: string;
  name: string;
}

export interface CompanyListItem {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  url: string | null;
  category_id: string | null;
  employees: string | null;
  description: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

interface CompaniesResponse {
  companies: CompanyListItem[];
  total: number;
  limit: number;
  offset: number;
}

interface CategoriesResponse {
  data: Category[];
}

interface UseCompaniesListAPIOptions {
  initialLimit?: number;
}

interface UseCompaniesListAPIReturn {
  companies: CompanyListItem[];
  categories: Category[];
  total: number;
  loading: boolean;
  error: string | null;
  // Filters
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string | null) => void;
  selectedEmployees: string | null;
  setSelectedEmployees: (employees: string | null) => void;
  showArchived: boolean;
  setShowArchived: (show: boolean) => void;
  // Pagination
  limit: number;
  offset: number;
  setOffset: (offset: number) => void;
  hasMore: boolean;
  loadMore: () => void;
  // Actions
  refetch: () => Promise<void>;
  // Derived data
  filteredCompanies: CompanyListItem[];
  uniqueEmployees: string[];
  getCategoryName: (categoryId: string | null) => string;
}

// Standard employee ranges
const EMPLOYEE_RANGES = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10001+'
];

export function useCompaniesListAPI(options: UseCompaniesListAPIOptions = {}): UseCompaniesListAPIReturn {
  const { initialLimit = 100 } = options;

  // State
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Pagination
  const [limit] = useState(initialLimit);
  const [offset, setOffset] = useState(0);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${VAULT_API_BASE}/categories`, {
        method: 'GET',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[useCompaniesListAPI] Failed to fetch categories');
        return;
      }

      const data: CategoriesResponse = await response.json();
      setCategories(data.data || []);
    } catch (err) {
      console.error('[useCompaniesListAPI] Error fetching categories:', err);
    }
  }, []);

  // Fetch companies
  const fetchCompanies = useCallback(async (append = false) => {
    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(append ? offset : 0));

      if (selectedCategoryId) {
        params.set('category_id', selectedCategoryId);
      }
      if (selectedEmployees) {
        params.set('employees', selectedEmployees);
      }
      if (searchQuery.trim()) {
        params.set('search', searchQuery.trim());
      }
      const url = `${VAULT_API_BASE}/companies?${params.toString()}`;
      console.log('[useCompaniesListAPI] Fetching:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-API-Key': VAULT_API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch companies: ${response.status}`);
      }

      const data: CompaniesResponse = await response.json();

      if (append) {
        setCompanies(prev => [...prev, ...data.companies]);
      } else {
        setCompanies(data.companies);
      }

      setTotal(data.total);
      console.log('[useCompaniesListAPI] Fetched', data.companies.length, 'companies, total:', data.total);
    } catch (err) {
      console.error('[useCompaniesListAPI] Error fetching companies:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  }, [limit, offset, selectedCategoryId, selectedEmployees, searchQuery]);

  // Load more (pagination)
  const loadMore = useCallback(() => {
    const newOffset = offset + limit;
    setOffset(newOffset);
  }, [offset, limit]);

  // Refetch
  const refetch = useCallback(async () => {
    setOffset(0);
    await fetchCompanies(false);
  }, [fetchCompanies]);

  // Get category name by ID
  const getCategoryName = useCallback((categoryId: string | null): string => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Unknown';
  }, [categories]);

  // Initial fetch
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch companies when filters change
  useEffect(() => {
    setOffset(0);
    fetchCompanies(false);
  }, [selectedCategoryId, selectedEmployees, searchQuery]);

  // Fetch more when offset changes (for pagination)
  useEffect(() => {
    if (offset > 0) {
      fetchCompanies(true);
    }
  }, [offset]);

  // Derived: has more pages
  const hasMore = useMemo(() => {
    return companies.length < total;
  }, [companies.length, total]);

  // Derived: unique employees from current dataset (for filter options)
  const uniqueEmployees = useMemo(() => {
    return EMPLOYEE_RANGES;
  }, []);

  // Client-side filtered companies — hide archived by default
  const filteredCompanies = useMemo(() => {
    if (showArchived) return companies;
    return companies.filter(c => !c.archived);
  }, [companies, showArchived]);

  return {
    companies,
    categories,
    total,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    selectedCategoryId,
    setSelectedCategoryId,
    selectedEmployees,
    setSelectedEmployees,
    limit,
    offset,
    setOffset,
    hasMore,
    loadMore,
    refetch,
    filteredCompanies,
    uniqueEmployees,
    getCategoryName,
    showArchived,
    setShowArchived
  };
}
